import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Building2, Home, FileText, Briefcase, Gavel, ShieldAlert, AlertTriangle, MapPin, Search, Quote, Linkedin, Youtube } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import { DEMO_THEMES } from "@/data/demoThemes";

const NAVY = "#0B2447";
const GOLD = "#C6A15B";
const CREAM = "#F5F3EE";
const serif = { fontFamily: "'Playfair Display', serif" };
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const IMG = {
  hero: "https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&w=1100&q=80&ixlib=rb-4.1.0",
  a1: "https://images.unsplash.com/photo-1600878459138-e1123b37cb30?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0",
  a2: "https://images.unsplash.com/photo-1609436132311-e4b0c9370469?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0",
  a3: "https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg?auto=compress&cs=tinysrgb&w=500&q=80",
  i1: "https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&w=400&q=80&ixlib=rb-4.1.0",
  i2: "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&w=400&q=80&ixlib=rb-4.1.0",
  i3: "https://images.unsplash.com/photo-1603294278610-b5bd0506303e?crop=entropy&cs=srgb&fm=jpg&w=400&q=80&ixlib=rb-4.1.0",
};
const AREAS = [
  { icon: Building2, name: "Business Law" },
  { icon: Home, name: "Real Estate" },
  { icon: FileText, name: "Estate Planning" },
  { icon: Briefcase, name: "Employment Law" },
  { icon: Gavel, name: "Civil Litigation" },
  { icon: ShieldAlert, name: "Personal Injury" },
];
const ATTORNEYS = [
  { img: IMG.a1, name: "Jonathan M. Hartwell", role: "Managing Partner", desc: "Commercial law, complex disputes, and strategic counsel." },
  { img: IMG.a2, name: "Priya S. Kapoor", role: "Partner", desc: "Business transactions, real estate, and emerging growth companies." },
  { img: IMG.a3, name: "Marcus T. Pierce", role: "Partner", desc: "Employment law, litigation, and workplace strategy." },
];
const INSIGHTS = [
  { img: IMG.i1, date: "APR 12, 2024", title: "Five Considerations Before Buying Commercial Real Estate" },
  { img: IMG.i2, date: "APR 3, 2024", title: "What Employers Need to Know About Workplace Accommodations" },
  { img: IMG.i3, date: "MAR 28, 2024", title: "Estate Planning in a Changing World" },
];
const Cap = ({ children }) => <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: GOLD }}>{children}</span>;

export default function LawDemo() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Hartwell & Pierce LLP (Demo) | WebsiteTech.pro"; }, []);

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: NAVY, ...body }} data-testid="law-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5" style={{ background: "#07152e", color: "#F9FAFB" }}>
        WebsiteTech.pro Demo · Law Firm — click to view all industry demos
      </Link>
      <div className="hidden sm:block text-white text-xs" style={{ background: "#07152e" }}>
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <span className="flex items-center gap-2 opacity-90"><Phone size={12} /> (312) 555-0187 <span className="opacity-50">·</span> <MapPin size={12} /> Chicago, IL</span>
          <span className="flex items-center gap-4 opacity-90">
            <Search size={13} className="opacity-70" />
            <span className="hover:opacity-100 cursor-pointer" style={{ color: GOLD }}>Client Portal →</span>
          </span>
        </div>
      </div>

      <DemoTopNav
        testid="law-nav"
        logo={<div className="leading-tight"><div className="text-lg tracking-[0.15em]" style={{ ...serif, color: NAVY }}>HARTWELL &amp; PIERCE</div><div className="text-[9px] tracking-[0.35em] uppercase" style={{ color: GOLD }}>People · Perspective · Possible</div></div>}
        items={["Practice Areas", "Our Attorneys", "Results", "Insights", "About", "Contact"]}
        ctaLabel="Request a Consultation"
        theme={{ bg: "rgba(245,243,238,0.92)", text: NAVY, accent: NAVY, accentText: GOLD, border: "#e2ddd0" }}
      />

      {/* hero */}
      <header data-testid="law-hero" className="relative overflow-hidden" style={{ background: NAVY }}>
        <img src={IMG.hero} alt="Attorneys" className="absolute inset-y-0 right-0 h-full w-1/2 object-cover opacity-70 hidden md:block" />
        <div className="absolute inset-0 hidden md:block" style={{ background: "linear-gradient(90deg, #0B2447 48%, rgba(11,36,71,0.55) 70%, rgba(11,36,71,0.15))" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-white">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="max-w-xl">
            <Cap>Lawyers for what's next</Cap>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={serif}>Experienced counsel.<br /><span style={{ color: GOLD }}>Clear direction.</span></h1>
            <p className="mt-6 text-lg opacity-85 max-w-lg">Strategic legal guidance for businesses, families, and individuals.</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button data-testid="law-consult-btn" className="rounded-sm px-7 py-3.5 text-xs tracking-[0.2em] uppercase font-semibold flex items-center gap-2" style={{ background: GOLD, color: NAVY }}>Request a Consultation <ArrowRight size={14} /></button>
              <button className="rounded-sm px-7 py-3.5 text-xs tracking-[0.2em] uppercase" style={{ border: `1px solid ${GOLD}66`, color: "#fff" }}>Explore Practice Areas</button>
            </div>
            <div className="mt-10 flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase opacity-70">
              People First <span style={{ color: GOLD }}>—</span> Practical Solutions <span style={{ color: GOLD }}>—</span> A Stronger Tomorrow
            </div>
          </motion.div>
        </div>
      </header>

      {/* practice areas — 6 in a row */}
      <section data-testid="law-practice-areas" className="max-w-7xl mx-auto px-6 -mt-8 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {AREAS.map((a, i) => (
            <motion.button key={a.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05, duration: 0.4 }}
              className="group bg-white rounded-sm px-4 py-4 border hover:shadow-lg transition-shadow flex items-center justify-between gap-2 text-left" style={{ borderColor: "#e2ddd0" }} data-testid={`law-area-${i}`}>
              <span className="flex items-center gap-2.5"><a.icon size={18} style={{ color: GOLD }} /><span className="text-sm font-semibold leading-tight">{a.name}</span></span>
              <ArrowRight size={14} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </motion.button>
          ))}
        </div>
      </section>

      {/* stats band (light) */}
      <section data-testid="law-stats" className="mt-16" style={{ background: "#EEEAE0" }}>
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-8">
          <div style={serif} className="text-2xl md:text-3xl leading-tight">TRUSTED ADVOCACY.<br />PROVEN EXPERIENCE.</div>
          <div className="flex items-center gap-10">
            <div><div className="text-3xl md:text-4xl font-bold" style={{ ...serif, color: NAVY }}>25+</div><div className="mt-1 text-xs uppercase tracking-widest opacity-60">Years of Service</div></div>
            <div className="h-12 w-px" style={{ background: "#d8d2c4" }} />
            <div><div className="text-3xl md:text-4xl font-bold" style={{ ...serif, color: NAVY }}>1,000+</div><div className="mt-1 text-xs uppercase tracking-widest opacity-60">Clients Served</div></div>
          </div>
          <div className="text-sm max-w-[220px]" style={serif}>A Broader Tomorrow<br />Through Stronger Counsel</div>
        </div>
      </section>

      {/* attorneys + insights + testimonial */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-3 gap-10">
        {/* attorneys */}
        <div data-testid="law-attorneys" className="lg:col-span-1">
          <div className="flex items-center justify-between mb-6"><Cap>Meet Our Attorneys</Cap><span className="text-xs tracking-widest uppercase" style={{ color: NAVY }}>View All →</span></div>
          <div className="space-y-5">
            {ATTORNEYS.map((a) => (
              <div key={a.name} className="flex gap-4" data-testid="law-attorney">
                <img src={a.img} alt={a.name} className="h-20 w-20 rounded-sm object-cover shrink-0" />
                <div>
                  <div style={serif} className="text-lg leading-tight">{a.name}</div>
                  <div className="text-[10px] tracking-widest uppercase mt-0.5" style={{ color: GOLD }}>{a.role}</div>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* insights */}
        <div data-testid="law-insights" className="lg:col-span-1">
          <div className="flex items-center justify-between mb-6"><Cap>Legal Insights</Cap><span className="text-xs tracking-widest uppercase" style={{ color: NAVY }}>View All →</span></div>
          <div className="space-y-4">
            {INSIGHTS.map((n) => (
              <div key={n.title} className="flex gap-4 items-center group cursor-pointer" data-testid="law-insight">
                <img src={n.img} alt="" className="h-16 w-20 rounded-sm object-cover shrink-0" />
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-slate-400">{n.date}</div>
                  <div style={serif} className="text-base leading-snug group-hover:opacity-70 transition-opacity">{n.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* testimonial */}
        <div className="lg:col-span-1">
          <Cap>Client Perspective</Cap>
          <div className="mt-6 rounded-sm p-7" style={{ background: "#EEEAE0" }} data-testid="law-testimonial">
            <Quote size={28} style={{ color: GOLD }} />
            <p className="mt-3 text-lg leading-relaxed" style={serif}>"Hartwell &amp; Pierce provided thoughtful, practical guidance during a complex transaction. Their team was responsive, clear, and genuinely invested in our success."</p>
            <div className="mt-5"><div className="font-semibold text-sm">A. Rodriguez</div><div className="text-xs text-slate-500">Business Owner, Chicago, IL</div></div>
          </div>
        </div>
      </section>

      {/* consultation CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="rounded-sm p-10 md:p-14 flex flex-wrap items-center justify-between gap-6 text-white" style={{ background: NAVY }}>
          <div>
            <Cap>Let's Move Forward Together</Cap>
            <h2 className="mt-3 text-3xl md:text-4xl" style={serif}>Request a Consultation</h2>
            <p className="mt-3 opacity-80 max-w-xl">We're here to listen and help you find a path forward. Our intake assistant can help you get started.</p>
          </div>
          <button className="rounded-sm px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-semibold whitespace-nowrap" style={{ background: GOLD, color: NAVY }}>Request a Consultation</button>
        </div>
      </section>

      {/* footer + disclaimer */}
      <footer data-testid="law-footer" className="text-white" style={{ background: "#07152e" }}>
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8 text-sm">
          <div><div className="text-lg tracking-[0.15em]" style={serif}>HARTWELL &amp; PIERCE</div><div className="text-[9px] tracking-[0.35em] uppercase mt-1" style={{ color: GOLD }}>People · Perspective · Possible</div><div className="flex gap-3 mt-5 opacity-70"><Linkedin size={16} /><Youtube size={16} /></div></div>
          <div><div className="uppercase tracking-widest text-xs opacity-80 mb-3">Practice</div><p className="opacity-70 leading-7">Business Law<br />Real Estate<br />Estate Planning<br />Employment Law</p></div>
          <div><div className="uppercase tracking-widest text-xs opacity-80 mb-3">Firm</div><p className="opacity-70 leading-7">Our Attorneys<br />Results<br />Insights<br />Client Portal</p></div>
          <div><div className="uppercase tracking-widest text-xs opacity-80 mb-3">Contact</div><p className="opacity-70 leading-7">233 South Wacker Drive<br />Chicago, IL 60606<br />(312) 555-0187<br />info@hartwellpierce.com</p></div>
        </div>
        <div data-testid="law-disclaimer" className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-4 text-xs flex items-start gap-2 opacity-80">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" style={{ color: GOLD }} />
            <span>Attorney Advertising. The information on this site and from our AI intake assistant is provided for general purposes only, is not legal advice, and does not create an attorney-client relationship. Prior results do not guarantee a similar outcome.</span>
          </div>
        </div>
      </footer>

      <AIConciergeWidget demo="law" concierge={DEMO_THEMES.law.concierge} />
    </div>
  );
}
